import { useEffect, useState } from "react";
import { Alert, Button, ButtonGroup, Card, CardBody, FormGroup, Input, Label, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { getInstallationSettings, updateInstallationSettings } from "../../services/installationService";
import BooleanIcon from "../common/BooleanIcon";
import ToggleSwitch from "../common/controls/ToggleSwitch/ToggleSwitch";

const MIN_STORES_PER_ACCOUNT = 1;
const MAX_STORES_PER_ACCOUNT = 9;

function validateCountryCodes(input) {
  if (!input.trim()) {
    return { valid: true, error: null, codes: [] };
  }

  const codes = input
    .split(",")
    .map((code) => code.trim())
    .filter(Boolean);

  const shortCode = codes.find((code) => code.length !== 2);
  if (shortCode) {
    return { valid: false, error: `"${shortCode}" must be exactly 2 characters`, codes: [] };
  }

  const nonUpperCode = codes.find((code) => !/^[A-Z]{2}$/.test(code));
  if (nonUpperCode) {
    return { valid: false, error: `"${nonUpperCode}" must be 2 uppercase letters (e.g., US, FR)`, codes: [] };
  }

  return { valid: true, error: null, codes };
}

export default function InstallationSettings() {
  const [settings, setSettings] = useState(null);
  const [settingsBeforeEdit, setSettingsBeforeEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allowListInput, setAllowListInput] = useState("");
  const [blockListInput, setBlockListInput] = useState("");
  const [allowListError, setAllowListError] = useState(null);
  const [blockListError, setBlockListError] = useState(null);

  const syncCountryInputs = (data) => {
    setAllowListInput((data.countries_allow_list ?? []).join(", "));
    setBlockListInput((data.countries_block_list ?? []).join(", "));
  };

  useEffect(() => {
    let cancelled = false;
    getInstallationSettings()
      .then(({ data }) => {
        if (cancelled) return;
        setSettings(data);
        syncCountryInputs(data);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load installation settings");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleEditMode = () => {
    if (editMode) {
      setSettings(settingsBeforeEdit);
      syncCountryInputs(settingsBeforeEdit);
      setAllowListError(null);
      setBlockListError(null);
    } else {
      setSettingsBeforeEdit({ ...settings });
    }
    setEditMode(!editMode);
  };

  const handleBooleanChange = (field, value) => setSettings((prev) => ({ ...prev, [field]: value }));

  const handleMaxStoresChange = (e) => {
    const value = Number(e.target.value);
    if (value < MIN_STORES_PER_ACCOUNT || value > MAX_STORES_PER_ACCOUNT || Number.isNaN(value)) {
      return;
    }
    setSettings((prev) => ({ ...prev, max_stores_per_account: value }));
  };

  const handleAllowListChange = (e) => {
    const { value } = e.target;
    setAllowListInput(value);
    const validation = validateCountryCodes(value);
    setAllowListError(validation.error);
  };

  const handleBlockListChange = (e) => {
    const { value } = e.target;
    setBlockListInput(value);
    const validation = validateCountryCodes(value);
    setBlockListError(validation.error);
  };

  const isValid = !allowListError && !blockListError;

  const handleSave = async () => {
    if (!settings || !isValid) {
      return;
    }

    const allowResult = validateCountryCodes(allowListInput);
    const blockResult = validateCountryCodes(blockListInput);
    if (!allowResult.valid || !blockResult.valid) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...settings,
        countries_allow_list: allowResult.codes,
        countries_block_list: blockResult.codes,
      };
      const { data } = await updateInstallationSettings(payload);
      setSettings(data);
      setSettingsBeforeEdit(null);
      syncCountryInputs(data);
      setEditMode(false);
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner size="sm" />;
  }

  if (!settings) {
    return <p className="text-danger">Failed to load settings.</p>;
  }

  const renderBoolean = (field) => {
    if (editMode) {
      return (
        <ToggleSwitch
          name={field}
          checked={settings[field]}
          onChange={(e) => handleBooleanChange(field, e.target.checked)}
        />
      );
    }
    return <BooleanIcon value={settings[field]} />;
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <Alert color="warning" className="mb-3">
        <strong>Warning:</strong> These settings directly affect production and can break functionality if configured
        incorrectly. Do not modify unless you fully understand the impact.
      </Alert>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <h5 className="mb-0 mr-3">RTI Automation Settings</h5>
          <ButtonGroup size="sm">
            <Button
              color={!editMode ? "primary" : "secondary"}
              outline={editMode}
              onClick={() => editMode && toggleEditMode()}
              disabled={saving}
            >
              View
            </Button>
            <Button
              color={editMode ? "primary" : "secondary"}
              outline={!editMode}
              onClick={() => !editMode && toggleEditMode()}
              disabled={saving}
            >
              Edit
            </Button>
          </ButtonGroup>
        </div>
        {editMode && (
          <Button color="primary" size="sm" onClick={handleSave} disabled={saving || !isValid}>
            {saving ? <Spinner size="sm" /> : "Save"}
          </Button>
        )}
      </div>

      <Card className="mb-3">
        <CardBody>
          <FormGroup className="d-flex align-items-center mb-2">
            {renderBoolean("enabled")}
            <div className="ml-3">
              <Label className="mb-0 font-weight-bold">
                Enable RTI Automation (leave enabled unless explicitly required otherwise)
              </Label>
            </div>
          </FormGroup>

          <hr />

          <FormGroup className="d-flex align-items-center mb-2">
            {renderBoolean("allow_resellers")}
            <div className="ml-3">
              <Label className="mb-0 font-weight-bold">Allow Resellers</Label>
            </div>
          </FormGroup>

          <hr />

          <FormGroup className="d-flex align-items-center mb-2">
            {renderBoolean("allow_key_accounts")}
            <div className="ml-3">
              <Label className="mb-0 font-weight-bold">Allow Key Accounts</Label>
            </div>
          </FormGroup>

          <hr />

          <FormGroup className="d-flex align-items-center mb-0">
            {editMode ? (
              <Input
                type="number"
                min={MIN_STORES_PER_ACCOUNT}
                max={MAX_STORES_PER_ACCOUNT}
                step={1}
                value={settings.max_stores_per_account}
                onChange={handleMaxStoresChange}
                style={{ width: 80 }}
              />
            ) : (
              <span className="font-weight-medium">{settings.max_stores_per_account}</span>
            )}
            <div className="ml-3">
              <Label className="mb-0 font-weight-bold">
                Max Stores Per Account ({MIN_STORES_PER_ACCOUNT} - {MAX_STORES_PER_ACCOUNT})
              </Label>
            </div>
          </FormGroup>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <Label className="font-weight-bold">Country Filters</Label>
          <p className="text-muted small">
            Comma-separated ISO 3166-1 alpha-2 codes. Empty allow list = all countries allowed. Block list takes
            precedence.
          </p>

          <FormGroup>
            <Label>Allow list</Label>
            <Input
              placeholder="e.g. US, CA, GB"
              value={allowListInput}
              onChange={handleAllowListChange}
              disabled={!editMode}
              invalid={!!allowListError}
            />
            {allowListError && <p className="text-danger small mt-1">{allowListError}</p>}
          </FormGroup>

          <FormGroup className="mb-0">
            <Label>Block list</Label>
            <Input
              placeholder="e.g. FR, BE"
              value={blockListInput}
              onChange={handleBlockListChange}
              disabled={!editMode}
              invalid={!!blockListError}
            />
            {blockListError && <p className="text-danger small mt-1">{blockListError}</p>}
          </FormGroup>
        </CardBody>
      </Card>
    </div>
  );
}
